import { Box } from "@mui/material";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_MEDIA,
  SITE_QUERY,
} from "src/domains/shared/constants/styles/site";
import { DroneSquareMark } from "../SiteShell/DroneSquareMark";
import { SiteImage } from "./media/SiteImage";
import type { SiteImageName } from "./media/siteMedia";
import { useReducedMotion } from "./motion/reducedMotion";
import { revealRef } from "./motion/reveal";
import { usePageVisible } from "./motion/usePageVisible";
import { SiteTitle } from "./SiteText";

/** 단계 하나가 켜져 있는 시간·마지막 단계에서 머무는 시간·처음으로 돌아가기 전 쉼(ms) */
const STEP_MS = 1000;
const HOLD_MS = 2200;
const RESTART_GAP_MS = 500;
const START_DELAY_MS = 350;
/** 진행선이 차는 시간은 단계 시간에서 이만큼 빼고, 그만큼 늦게 시작한다 */
const FILL_LEAD_MS = 300;
const VISIBLE_THRESHOLD = 0.15;

/** 단계 그림 — 사진 한 장, 또는 마지막 「플랫폼」 단계의 DroneSquare 앱 마크 */
type FlowArt =
  | { kind: "photo"; name: SiteImageName; position?: string }
  | { kind: "mark" };

/** 드론 측량 6단계 그림(시안 순서) — 문구는 `site.flow.steps[i]` */
const FLOW_ART: readonly FlowArt[] = [
  { kind: "photo", name: "airspace_chart", position: "60% 50%" },
  { kind: "photo", name: "drone_sky" },
  { kind: "photo", name: "station" },
  { kind: "photo", name: "interchange" },
  { kind: "photo", name: "step5_3d" },
  { kind: "mark" },
];

const FLOW_STYLES = {
  list: {
    "--fg": "clamp(14px,1.8vw,26px)",
    "--dot": "30px",
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gridTemplateColumns: "repeat(6,minmax(0,1fr))",
    gap: "0 var(--fg)",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "minmax(0,1fr)", gap: 0 },
  },
  step: {
    minWidth: 0,
    '&[data-state="on"] img, &[data-state="done"] img, &[data-state="on"] svg, &[data-state="done"] svg':
      {
        filter: "grayscale(0)",
      },
    '&[data-state="on"] [data-dot], &[data-state="done"] [data-dot]': {
      background: SITE_COLOR.blue,
      borderColor: SITE_COLOR.blue,
      color: "#fff",
    },
    '&[data-state="on"] h3': { color: SITE_COLOR.blue },
    "&:last-of-type [data-line]": { display: "none" },
    [SITE_MEDIA.tablet]: {
      position: "relative",
      display: "grid",
      gridTemplateColumns: "var(--dot) 112px minmax(0,1fr)",
      columnGap: "16px",
      alignItems: "center",
      height: "102px",
      paddingBottom: "18px",
      boxSizing: "border-box",
      "&:last-of-type": { height: "84px", paddingBottom: 0 },
    },
  },
  /** 설명이 붙는 절차(드론 측량 페이지): 점은 사진 위쪽에 맞추고 세로선을 다음 행의 점까지 늘인다 */
  describedStep: {
    [SITE_MEDIA.tablet]: {
      height: "auto",
      alignItems: "start",
      paddingBottom: "22px",
      "&:last-of-type": { height: "auto", paddingBottom: 0 },
      "& [data-rail]": { marginTop: "27px" },
      "& [data-text]": { gridColumn: 3, gridRow: 1 },
      "& [data-line]": {
        top: "calc(27px + var(--dot) + 6px)",
        bottom: "calc(6px - 27px)",
        height: "auto",
      },
    },
  },
  image: {
    margin: 0,
    aspectRatio: "4/5",
    borderRadius: "16px",
    overflow: "hidden",
    background: SITE_COLOR.panel,
    boxShadow: "0 0 0 1px rgba(15,23,42,.07),0 6px 18px rgba(28,44,66,.08)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      filter: "grayscale(1)",
      transition: "filter .7s ease",
    },
    [SITE_MEDIA.tablet]: {
      gridColumn: 2,
      gridRow: 1,
      aspectRatio: "auto",
      height: "84px",
      borderRadius: "12px",
    },
  },
  /** 사진 자리에 앉는 앱 마크 — 사진처럼 흑백에서 색으로 */
  mark: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    "& svg": {
      width: "44%",
      height: "auto",
      filter: "grayscale(1)",
      transition: "filter .7s ease",
    },
    [SITE_MEDIA.tablet]: { "& svg": { width: "auto", height: "56%" } },
  },
  rail: {
    position: "relative",
    height: "var(--dot)",
    margin: "20px 0 12px",
    [SITE_MEDIA.tablet]: {
      gridColumn: 1,
      gridRow: 1,
      position: "static",
      margin: 0,
    },
  },
  dot: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    placeItems: "center",
    width: "var(--dot)",
    height: "var(--dot)",
    borderRadius: "50%",
    background: "#fff",
    border: `1.5px solid ${SITE_COLOR.line}`,
    fontSize: "12px",
    fontWeight: 700,
    color: SITE_COLOR.ink3,
    fontVariantNumeric: "tabular-nums",
    transition: "background-color .35s,border-color .35s,color .35s",
  },
  line: {
    position: "absolute",
    top: "50%",
    left: "calc(var(--dot) + 8px)",
    right: "calc(8px - var(--fg))",
    height: "2px",
    marginTop: "-1px",
    borderRadius: "2px",
    background: SITE_COLOR.line2,
    overflow: "hidden",
    "& i": {
      position: "absolute",
      inset: 0,
      background: SITE_COLOR.blue,
      transform: "scaleX(0)",
      transformOrigin: "left center",
    },
    [SITE_MEDIA.tablet]: {
      left: "calc(var(--dot) / 2 - 1px)",
      right: "auto",
      top: "calc(42px + var(--dot) / 2 + 6px)",
      width: "2px",
      height: "calc(102px - var(--dot) - 12px)",
      margin: 0,
      "& i": { transform: "scaleY(0)", transformOrigin: "center top" },
    },
  },
  title: {
    margin: 0,
    fontSize: "clamp(15px,1.25vw,18px)",
    lineHeight: 1.4,
    fontWeight: 700,
    letterSpacing: "-.02em",
    color: SITE_COLOR.ink,
    textWrap: "balance",
    transition: "color .35s",
    [SITE_MEDIA.tablet]: { gridColumn: 3, gridRow: 1, fontSize: "16px" },
  },
  text: {
    margin: "6px 0 0",
    fontSize: "14px",
    lineHeight: 1.6,
    color: SITE_COLOR.ink2,
  },
} as const;

interface FlowStepText {
  title: string;
  text: string;
  alt: string;
}

export interface ServiceFlowProps {
  /** 단계마다 설명을 붙인다(드론 측량 페이지) */
  withDescriptions?: boolean;
}

/**
 * 드론 측량 진행 절차 6단계(시안 `.flow`): 화면에 들어오면 01 → 06 순서로 점이 켜지고 진행선이 차며 사진이
 * 흑백에서 색으로 바뀐다. 마지막 단계에서 잠시 머문 뒤 처음부터 다시. 화면 밖·탭 숨김이면 멈추고 처음으로 되돌린다.
 */
export function ServiceFlow({ withDescriptions = false }: ServiceFlowProps) {
  const { t } = useTranslation("landing");
  const steps = t("site.flow.steps", {
    returnObjects: true,
  }) as readonly FlowStepText[];
  const listRef = useRef<HTMLOListElement>(null);
  const [active, setActive] = useState(-1);
  const [visible, setVisible] = useState(false);
  const pageVisible = usePageVisible();
  const reducedMotion = useReducedMotion();
  const running = visible && pageVisible && !reducedMotion;
  // 움직임 줄이기면 모든 단계를 켠 채로, 멈춰 있으면(화면 밖·탭 숨김) 처음 상태로 보인다
  const shownStep = (() => {
    if (reducedMotion) return steps.length;
    return running ? active : -1;
  })();

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      {
        threshold: VISIBLE_THRESHOLD,
      }
    );
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  // 단계 진행: 보이는 동안만 타이머로 다음 단계, 마지막 뒤에는 쉬었다가 처음부터
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const fills = [...list.querySelectorAll<HTMLElement>("[data-line] i")];
    const animations: Animation[] = [];
    const vertical = window.matchMedia(SITE_QUERY.tablet).matches;
    const axis = vertical ? "scaleY" : "scaleX";
    const fill = (index: number, duration: number, delay: number) => {
      const target = fills[index];
      if (!target) return;
      animations.push(
        target.animate(
          [{ transform: `${axis}(0)` }, { transform: `${axis}(1)` }],
          {
            duration,
            delay,
            easing: SITE_EASE.inOut,
            fill: "forwards",
          }
        )
      );
    };
    if (reducedMotion) {
      fills.forEach((_, index) => fill(index, 0, 0));
      return () => animations.forEach((animation) => animation.cancel());
    }
    if (!running) return undefined;
    let timer = 0;
    let index = -1;
    const next = () => {
      index += 1;
      if (index === steps.length) {
        animations.forEach((animation) => animation.cancel());
        animations.length = 0;
        index = -1;
        setActive(-1);
        timer = window.setTimeout(next, RESTART_GAP_MS);
        return;
      }
      setActive(index);
      fill(index, STEP_MS - FILL_LEAD_MS, FILL_LEAD_MS);
      timer = window.setTimeout(
        next,
        index === steps.length - 1 ? HOLD_MS : STEP_MS
      );
    };
    timer = window.setTimeout(next, START_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      animations.forEach((animation) => animation.cancel());
      // 다시 돌 때 지난 회차의 단계가 잠깐 보이지 않게 처음으로
      setActive(-1);
    };
  }, [running, reducedMotion, steps.length]);

  const stateOf = (index: number) => {
    if (index < shownStep) return "done";
    if (index === shownStep) return "on";
    return undefined;
  };

  return (
    <Box
      component="ol"
      ref={listRef}
      aria-label={t("site.flow.label")}
      sx={FLOW_STYLES.list}
    >
      {steps.map((step, index) => {
        const art = FLOW_ART[index];
        return (
          <Box
            component="li"
            key={step.title}
            ref={revealRef}
            data-reveal="up"
            data-state={stateOf(index)}
            style={{ "--i": index } as CSSProperties}
            sx={[
              FLOW_STYLES.step,
              withDescriptions && FLOW_STYLES.describedStep,
            ]}
          >
            <Box component="figure" sx={FLOW_STYLES.image}>
              {art.kind === "mark" ? (
                <Box role="img" aria-label={step.alt} sx={FLOW_STYLES.mark}>
                  <DroneSquareMark />
                </Box>
              ) : (
                <SiteImage
                  name={art.name}
                  sizes="(max-width: 900px) 120px, 220px"
                  alt={step.alt}
                  style={
                    art.position ? { objectPosition: art.position } : undefined
                  }
                />
              )}
            </Box>
            <Box data-rail="" sx={FLOW_STYLES.rail}>
              <Box component="span" data-dot="" sx={FLOW_STYLES.dot}>
                {String(index + 1).padStart(2, "0")}
              </Box>
              <Box component="span" data-line="" sx={FLOW_STYLES.line}>
                <i />
              </Box>
            </Box>
            {withDescriptions ? (
              <div data-text="">
                <Box component="h3" sx={FLOW_STYLES.title}>
                  <SiteTitle text={step.title} />
                </Box>
                <Box component="p" sx={FLOW_STYLES.text}>
                  {step.text}
                </Box>
              </div>
            ) : (
              <Box component="h3" sx={FLOW_STYLES.title}>
                <SiteTitle text={step.title} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
